import type { NodeScene } from "./nodeTypes";
import type { ContentType } from "./dataTypes/ContentType";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useMemo } from "react";

const UPCASABLE_ELEMENTS = new Set(["true", "false"]);

/**
 * Pure function to format content type names (same as Unity).
 * Can be used anywhere without React hooks dependency.
 */
export const formatContentTypeName = (modelName: string | undefined | null): string => {
  if (!modelName) return "-";

  const normalized = modelName.toLowerCase().replace(/-/g, "_");

  const mapping: Record<string, string> = {
    content_audio: "Audio",
    content_button_1: "Button",
    content_button_reload: "Reload Button",
    content_button_2: "Countdown Button",
    content_clean: "Clear",
    content_hotspot_scene_image: "Hotspot Image",
    content_hotspot_scene_video: "Hotspot",
    content_onboarding: "Image 2D",
    content_photo_catch: "Photo Catch",
    content_quizz_1: "Quizz text",
    content_quizz_2: "Quizz photo",
    content_quizz_3: "Quizz text",
    content_scoring_general: "Scoring General",
    content_scoring_scene: "Scoring Scene",
    content_score_switch: "Score Switch",
    content_text: "Text",
    content_thumbnail: "Thumbnail",
    content_tuto: "Tuto",
    content_tuto2: "Tuto 2",
    content_tuto_2: "Tuto 2",
    content_video_2d: "Video 2D",
    content_video_2_d: "Video 2D",
    content_vr_locator: "VR Locator",
    content_zone_detect: "Zone Detect",
    content_zone_sort: "Zone Sort",
    content_quizz_sort: "Quizz Sort",
    content_speech_to_check: "Speech AI",
    content_pdf_viewer: "PDF Viewer",
    content_3d_viewer: "3D Viewer",
    content_3_d_viewer: "3D Viewer",
    content_yes_no_question: "Yes/No question",
    content_blur: "Blur",
    content_scene: "Scene",
    content_danger_catch: "Danger Catch",
    content_highlight: "Highlight",
    content_random_quizz: "Random Quizz",
    content_element_catch: "Element Catch",
    content_fire_extinguisher: "Fire Extinguisher",
    content_fire: "Fire",
    content_quizz_image_multiple: "Quizz Image",
    content_avatar_ai: "Avatar AI",
    content_avatar_ai_persona: "People AI",
    content_dust: "Dust",
    content_avatar_ai_session_debrief: "Debrief AI",
    content_avatar_audio_player: "Avatar Audio Player",
  };

  return mapping[normalized] || modelName;
};

export const useNameFormatter = () => {
  const { t } = useTranslation();
  return useMemo(() => new NameFormatter(t), [t]);
};

class NameFormatter {
  constructor(private readonly t: TFunction) {}

  getHumanizedNodeNameFromNode = (node: NodeScene): string => {
    const { name } = node.details;
    return this.getHumanizedNodeName(name);
  };

  getFullName = (name: string | undefined | null): string => {
    if (!name) return "";
    const prefixStart = name.indexOf("_");
    return prefixStart > -1 ? name.slice(prefixStart + 1) : name;
  };

  getFullNodeNameFromNode = (node: NodeScene): string => this.getFullName(node.details.name);

  getHumanizedNodeNameFromContentType = (contentType: ContentType) => {
    const { name } = contentType.object;

    return this.getHumanizedNodeName(name);
  };

  getHumanizedNodeName = (name: string | undefined | null) => {
    if (!name) return "";

    // special case of the START NODE
    /*
    if (isStartScene) {
      return this.t("graph.nodes.startScene");
    } */

    // now we try to "build" a meaningful name (prefix = some string ending with '_')
    // 1) we remove any prefix (such as "discoVRy_")
    const prefixStart = name.indexOf("_");
    if (prefixStart > -1) {
      name = name.slice(prefixStart + 1);
    }

    // 2) then we split words and keep at most 2 of them
    let elements = name.split(/\s+/g).slice(0, 2);
    elements = elements.flatMap((element) => element.split(/(?=[A-Z]+)/g)).slice(0, 2);

    // 3) optionally we detect "sequenced" names
    if (elements.length === 1 && /-\d+$/.test(elements[0])) {
      const [firstElement] = elements;
      const suffixStart = firstElement.lastIndexOf("-");
      elements = [firstElement.slice(0, suffixStart), `(${firstElement.slice(suffixStart + 1)})`];
    }

    return elements.join(" ");
  };

  getHumanizedFieldName = (fieldName: string) => {
    if (!fieldName) return "";
    const elements = fieldName.split(/[-_\s]+/g);
    const capitalizedElements = [
      // Capitalize the beginning of the sentence
      (elements[0] = this.capitalize(elements[0])),
      ...elements
        .slice(1)
        .map((element) =>
          UPCASABLE_ELEMENTS.has(element)
            ? element.toLocaleUpperCase()
            : element.toLocaleLowerCase(),
        ),
    ];
    // detect questions and add a question mark
    if (capitalizedElements[0] === "Is") {
      capitalizedElements.push("?");
    }
    return capitalizedElements.join(" ");
  };

  getFormattedModelName = (modelName: string) => {
    if (modelName === "content-clean") {
      modelName = "content-clear";
    }

    let elements = modelName.split("-");
    if (elements[0] === "content") {
      elements = elements.slice(1);
    }

    return elements.map(this.capitalize).join(" ");
  };

  private capitalize = (s?: string) => {
    if (!s) return "";
    return s[0].toLocaleUpperCase() + s.slice(1).toLocaleLowerCase();
  };

  getSameFormattedModelNameThanUnity = (modelName: string) => {
    const { t } = this;

    const contentTypeResult = formatContentTypeName(modelName);
    if (contentTypeResult !== modelName) {
      return contentTypeResult;
    }

    switch (modelName) {
      case "project.id_background_music":
        return `${t("pages.project.backgroundMusic")}`;
      case "project.source_thumbnail":
        return `${t("pages.gallery.deleteMedia.moduleLogo")}`;
      case "project.source_logo":
        return `${t("pages.gallery.deleteMedia.moduleLogo")}`;
      case "project-language-datum.media_image_id":
      case "project-language-datum.media_audio_id":
      case "project-language-datum.media_answer1_id":
      case "project-language-datum.media_answer2_id":
      case "project-language-datum.media_answer3_id":
        return `${t("pages.gallery.deleteMedia.languageData")}`;
      case "project-group.source_thumbnail":
        return `${t("pages.gallery.deleteMedia.projectGroupLogo")}`;
      default:
        return "Unknown";
    }
  };
}

export default NameFormatter;
